import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { APIContext } from 'context/APIContext'
import { ModalContext, ModalKey } from 'components/Modals/ModalContext'
import { ChevronRightIcon } from '@heroicons/react/20/solid'
import kinesisLogo from 'media/kinesis_logo.png'

import ScreenFrame from "components/Frame/ScreenFrame"
import SearchBar from 'components/Search/SearchBar'
import BrandColorButton from 'components/Buttons/BrandColorButton'
import ContentFrame from "components/Frame/ContentFrame"

export default function ExerciseCollectionsView() {
    
    const navigate = useNavigate()

    const { GetExerciseCollectionList, CreateExerciseCollection } = useContext(APIContext)

    const { ShowModal } = useContext(ModalContext)

    const [searchTerm, setSearchTerm] = useState('')
    const [exerciseCollectionList, setExerciseCollectionList] = useState(null)
    const [filteredExerciseCollections, setFilteredExerciseCollections] = useState([])

    const LoadExerciseCollectionList = async () => {
        setExerciseCollectionList(null)
        const result = await GetExerciseCollectionList()
        setExerciseCollectionList(result)
    }

    const FilterExerciseCollectionsBySearchTerm = (item) => {
        const term = searchTerm.toLowerCase()
        return (
            item.name.toLowerCase().includes(term) ||
            item.description.toLowerCase().includes(term)
        )
    }

    const OnCreateExerciseCollectionButton = () => {
        const modalData = {
            key: ModalKey.TextAreaAction,
            size: 'third',
            titleText: 'Create New Exercise Collection',
            bodyText: ['Enter the name of the new exercise collection'],
            textAreaPlaceholder: 'Enter name here',
            buttonText: 'Create Collection',
            buttonAction: async (text) => {
                const exerciseCollectionId = await CreateExerciseCollection(text)
                if (exerciseCollectionId !== null) {
                    navigate(`/exerciseCollection/${exerciseCollectionId}`)
                }
            }
        }

        ShowModal(modalData)
    }

    useEffect(() => { LoadExerciseCollectionList() }, [])

    useEffect(() => {
        if (exerciseCollectionList !== null) {
            const filtered = exerciseCollectionList.filter(item => FilterExerciseCollectionsBySearchTerm(item))

            setFilteredExerciseCollections(filtered)
        }
    }, [exerciseCollectionList, searchTerm])

    if (exerciseCollectionList === null) {
        return (
            <ScreenFrame>
                <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                <div className='w-full h-[800px] bg-gray-200 rounded-lg animate-pulse' />
            </ScreenFrame>
        )
    }

    return (
        <ScreenFrame>
            <div className='flex items-center gap-x-4'>
                <h1 className="text-2xl font-semibold text-gray-900 tracking-tight pt-2 flex-grow">
                    Exercise Collections
                </h1>

                <div className='w-40'>
                    <BrandColorButton
                        size='small'
                        text="Create"
                        className='shadow-md'
                        action={() => OnCreateExerciseCollectionButton()}
                    />
                </div>
            </div>

            <SearchBar
                placeholder="Enter Collection Name or Description"
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                showFiltersToggle={false}
            />

            <ContentFrame className='divide-y divide-gray-200'>
                {filteredExerciseCollections.length === 0 &&
                    <div className="text-center text-gray-500 py-4">No exercise collections found</div>
                }

                {filteredExerciseCollections.length > 0 && filteredExerciseCollections.map((exerciseCollection, index) => (
                    <li
                        key={index}
                        className='w-full flex items-center px-6 py-4 gap-x-6 cursor-pointer hover:bg-gray-50'
                        onClick={() => navigate(`/exerciseCollection/${exerciseCollection.exerciseCollectionId}`)}
                    >
                        <div>
                            <img src={kinesisLogo} alt="Kinesis Logo" className="h-12 w-12" />
                        </div>

                        <div className="flex-grow">
                            <p className="font-lg font-semibold">{exerciseCollection.name}</p>

                            {exerciseCollection.description.length > 0 && <p className="text-sm text-gray-600">{exerciseCollection.description}</p>}
                            {exerciseCollection.description.length === 0 && <p className="text-sm text-gray-600">No Description</p>}
                        </div>

                        <ChevronRightIcon className="text-gray-400 h-8" />
                    </li>
                ))}
            </ContentFrame>
        </ScreenFrame>
    )
}